import type { CaptureSource, CurveParams, EffortLevel, RecurrencePattern } from '@app/core';

import type { AppDatabase } from './database.js';

interface SeedItem {
  text: string;
  source: CaptureSource;
  category?: string;
  consequence?: string;
  effort?: EffortLevel;
  curve: CurveParams;
  recurrence?: RecurrencePattern;
  createdAtOffset: number; // days before now
}

function daysAgo(days: number): Date {
  return new Date(Date.now() - days * 86_400_000);
}

function daysFromNow(days: number): Date {
  return new Date(Date.now() + days * 86_400_000);
}

const SEED_ITEMS: SeedItem[] = [
  // Warming — approaching deadline
  {
    text: 'File quarterly taxes',
    source: 'onboarding-braindump',
    category: 'finance',
    consequence: 'Late penalty + interest',
    effort: 'deep',
    curve: { direction: 'warming', startingTemperature: 25, ceiling: 95, easing: 'ease-in', anchorDate: daysFromNow(5) },
    createdAtOffset: 20,
  },
  // Warming — deadline passed (will transition to cooling)
  {
    text: 'Submit insurance claim',
    source: 'quick-add',
    category: 'finance',
    consequence: 'Claim expires',
    effort: 'moderate',
    curve: { direction: 'warming', startingTemperature: 20, ceiling: 80, easing: 'ease-in', anchorDate: daysAgo(2) },
    createdAtOffset: 14,
  },
  // Cooling — no deadline, moderate urgency
  {
    text: 'Call dentist for checkup',
    source: 'onboarding-conversation',
    category: 'health',
    effort: 'quick',
    curve: { direction: 'cooling', startingTemperature: 55, easing: 'ease-in', duration: 60 },
    createdAtOffset: 10,
  },
  // Cooling — aspirational, low temp
  {
    text: 'Learn to play guitar',
    source: 'onboarding-braindump',
    category: 'personal',
    effort: 'deep',
    curve: { direction: 'cooling', startingTemperature: 30, easing: 'ease-in', duration: 120 },
    createdAtOffset: 30,
  },
  // Cooling — near fade threshold
  {
    text: 'Organize garage',
    source: 'onboarding-braindump',
    category: 'home',
    effort: 'deep',
    curve: { direction: 'cooling', startingTemperature: 15, easing: 'ease-in', duration: 20 },
    createdAtOffset: 18,
  },
  // Fixed recurring
  {
    text: 'Mow the lawn',
    source: 'onboarding-braindump',
    category: 'home',
    effort: 'moderate',
    curve: { direction: 'warming', startingTemperature: 15, ceiling: 75, easing: 'ease-in', anchorDate: daysFromNow(3) },
    recurrence: {
      type: 'fixed',
      interval: 1,
      unit: 'weeks',
      curve: { direction: 'warming', startingTemperature: 15, ceiling: 75, easing: 'ease-in' },
    },
    createdAtOffset: 4,
  },
  // Loose recurring
  {
    text: 'Call Mom',
    source: 'onboarding-conversation',
    category: 'family',
    effort: 'quick',
    curve: { direction: 'warming', startingTemperature: 0, ceiling: 55, easing: 'ease-in', duration: 26 },
    recurrence: { type: 'loose', restPeriod: 5, rampDuration: 21, ceiling: 55, easing: 'ease-in' },
    createdAtOffset: 8,
  },
  // Quick win — moderate temp
  {
    text: 'Reply to Marcus about code review',
    source: 'quick-add',
    category: 'work',
    effort: 'quick',
    curve: { direction: 'cooling', startingTemperature: 65, easing: 'ease-in', duration: 7 },
    createdAtOffset: 1,
  },
  // Warming — distant deadline
  {
    text: 'Renew passport',
    source: 'onboarding-braindump',
    category: 'personal',
    consequence: 'Trip cancelled',
    effort: 'moderate',
    curve: { direction: 'warming', startingTemperature: 15, ceiling: 85, easing: 'ease-in', anchorDate: daysFromNow(45) },
    createdAtOffset: 5,
  },
];

/**
 * Seeds the database with realistic mock items for development and testing.
 * Clears existing items before seeding.
 */
export async function seedDatabase(db: AppDatabase): Promise<void> {
  await db.items.clear();
  await db.interactions.clear();

  for (const seed of SEED_ITEMS) {
    await db.items.add({
      id: crypto.randomUUID(),
      text: seed.text,
      source: seed.source,
      category: seed.category,
      consequence: seed.consequence,
      effort: seed.effort,
      curve: seed.curve,
      recurrence: seed.recurrence,
      status: 'active',
      createdAt: daysAgo(seed.createdAtOffset),
      reheatCount: 0,
    });
  }
}
